import React, { RefObject, useEffect, useState } from 'react';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import { selectHasInitialScrolled, selectMainMenuScrollX, setHasInitialScrolled, setMainMenuScrollX } from '@reducers/header';
import { useHandleScroll } from '@v2/pages/common/hooks/useHandleScroll';
import { Dimensions, ScrollView, View } from 'react-native';
import { isPhone } from '@helpers';
import { navigationLevels, withFeatureFlags } from '@v2/pages/machines/common/constants';
import { role, scale } from '@components/frame';
import { HEADER_MOBILE_GAP_BETWEEN_LEVELS, SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';
import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';
import { NavLink } from './NavLink';
import FeatureFlags from '@components/featureFlags';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { MachinesScreen } from '@v2/pages/machines/common/enums';

export const MainMenu = () => {
  const { checkIsLinkSelected, handleNavigate, checkIsMainMenuItemAvailable } = useHandleNavigationMachinesHeader();

  const dispatch = useAppDispatch();

  const scrollX: number = useSelector(selectMainMenuScrollX);
  const hasInitialScrolled = useSelector(selectHasInitialScrolled);

  const { handleScroll, handleScrollEnd, scrollViewRef } = useHandleScroll(setMainMenuScrollX, scrollX);

  const [itemRefs, setItemRefs] = useState<Record<string, RefObject<View>>>({} as Record<string, RefObject<View>>);

  const createItemRef = (key: string) => {
    if (!itemRefs[key]) {
      itemRefs[key] = React.createRef();
      setItemRefs({ ...itemRefs });
    }
    return itemRefs[key];
  };

  useEffect(() => {
    if (!hasInitialScrolled && scrollViewRef.current && isPhone) {
      const scrollToSelectedItem = () => {
        const menuItems = Object.values(navigationLevels.context);

        const selectedIndex = menuItems.findIndex((menuItem) => {
          return checkIsLinkSelected(menuItem, true);
        });

        const fallbackKey = menuItems.find((menuItem) => checkIsLinkSelected(menuItem, true)) || '';

        const targetKey = selectedIndex !== -1 ? menuItems[selectedIndex] : fallbackKey;
        const selectedRef = itemRefs[targetKey];

        if (selectedRef?.current) {
          selectedRef.current.measureLayout(
            scrollViewRef.current,
            (x, y, width, height) => {
              const screenWidth = Dimensions.get('window').width;
              const rightOffset = 15;
              const scrollPosition = Math.max(0, x + width - screenWidth + rightOffset);

              scrollViewRef.current?.scrollTo({
                x: scrollPosition,
                animated: true,
              });
            },
            () => {
              // fallback
              const estimatedItemWidth = 120;
              const index = menuItems.findIndex((item) => item === targetKey);
              const estimatedPosition = index * estimatedItemWidth;
              const screenWidth = Dimensions.get('window').width;
              const rightOffset = 15;
              const scrollPosition = Math.max(0, estimatedPosition + estimatedItemWidth - screenWidth + rightOffset);

              scrollViewRef.current?.scrollTo({
                x: scrollPosition,
                animated: true,
              });
            }
          );
        }

        dispatch(setHasInitialScrolled(true));
      };

      const timeoutId = setTimeout(scrollToSelectedItem, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [hasInitialScrolled, itemRefs, navigationLevels.context]);

  return (
    <ScrollView
      ref={scrollViewRef}
      onScroll={handleScroll}
      onScrollEndDrag={handleScrollEnd}
      onMomentumScrollEnd={handleScrollEnd}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={[
        { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginHorizontal: 'auto' },
        isPhone && { paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL), paddingBottom: scale(HEADER_MOBILE_GAP_BETWEEN_LEVELS) },
      ]}
      scrollEventThrottle={16}
    >
      {Object.values(navigationLevels.context)
        .filter((title) => !isRestrictedForOperator(role || '', RESTRICTION_CATEGORIES.MACHINE_TABS, title))
        .filter(checkIsMainMenuItemAvailable)
        .map((title) => {
          const isSelected = checkIsLinkSelected(title, true);
          const featureFlagData = withFeatureFlags.find((i) => i.title === title);

          const mainMenuTestIDs: Record<string, string> = {
            [MachinesScreen.PERFORMANCE]: TEST_IDS.machinesHeader.mainMenuPerformance,
            [MachinesScreen.TIMELINE]: TEST_IDS.machinesHeader.mainMenuTimeline,
            [MachinesScreen.HISTORY]: TEST_IDS.machinesHeader.mainMenuHistory,
            [MachinesScreen.SCHEDULED]: TEST_IDS.machinesHeader.mainMenuScheduled,
          };

          const content = (
            <View ref={createItemRef(title)}>
              <NavLink title={title} selected={isSelected} onPress={() => handleNavigate(title)} testID={mainMenuTestIDs[title]} />
            </View>
          );

          return (
            <React.Fragment key={title}>
              {!!featureFlagData ? <FeatureFlags name={featureFlagData.featureFlagName}>{content}</FeatureFlags> : content}
            </React.Fragment>
          );
        })}
    </ScrollView>
  );
};
